var _a;
import { atom, createStore } from "jotai";
import { assertExists, assertEquals } from "@blocksuite/global/utils";
import { z } from "zod";
import { atomWithStorage } from "jotai/utils";
import { atomEffect } from "jotai-effect";
const contentLayoutBaseAtom = atom("editor");
const contentLayoutAtom = atom(
  (get) => get(contentLayoutBaseAtom),
  (_, set, layout) => {
    set(contentLayoutBaseAtom, (prev) => {
      let setV;
      if (typeof layout !== "function") {
        setV = () => layout;
      } else {
        setV = layout;
      }
      const nextValue = setV(prev);
      if (nextValue === "editor") {
        return nextValue;
      }
      if (nextValue.first !== "editor") {
        throw new Error("The first element of the layout should be editor.");
      }
      if (nextValue.splitPercentage && nextValue.splitPercentage < 70) {
        throw new Error("The split percentage should be greater than 70.");
      }
      return nextValue;
    });
  }
);
let rootStore = createStore();
function getCurrentStore() {
  return rootStore;
}
function _setCurrentStore(store) {
  rootStore = store;
}
const isBrowser = typeof window !== "undefined";
const isServer = !isBrowser && typeof navigator === "undefined";
const isDesktop = isBrowser && !!((_a = window.appInfo) == null ? void 0 : _a.electron);
class UaHelper {
  constructor(navigator2) {
    this.navigator = navigator2;
    this.isLinux = false;
    this.isMacOs = false;
    this.isSafari = false;
    this.isWindows = false;
    this.isFireFox = false;
    this.isMobile = false;
    this.isChrome = false;
    this.isIOS = false;
    this.getChromeVersion = () => {
      const raw = this.navigator.userAgent.match(/Chrom(e|ium)\/([0-9]+)\./);
      assertExists(raw);
      return parseInt(raw[2], 10);
    };
    this.uaMap = getUa(navigator2);
    this.initUaFlags();
  }
  checkUseragent(isUseragent) {
    return Boolean(this.uaMap[isUseragent]);
  }
  initUaFlags() {
    this.isLinux = this.checkUseragent("linux");
    this.isMacOs = this.checkUseragent("mac");
    this.isSafari = this.checkUseragent("safari");
    this.isWindows = this.checkUseragent("win");
    this.isFireFox = this.checkUseragent("firefox");
    this.isMobile = this.checkUseragent("mobile");
    this.isChrome = this.checkUseragent("chrome");
    this.isIOS = this.checkUseragent("ios");
  }
}
const getUa = (navigator2) => {
  const ua = navigator2.userAgent;
  const uas = ua.toLowerCase();
  const mobile = /iPhone|iPad|iPod|Android/i.test(ua);
  const android = mobile && (uas.indexOf("android") > -1 || uas.indexOf("linux") > -1) || uas.indexOf("adr") > -1;
  const ios = mobile && !android && /Mac OS/i.test(ua);
  const mac = !mobile && /Mac OS/i.test(ua);
  const iphone = ios && uas.indexOf("iphone") > -1;
  const ipad = ios && !iphone;
  const wx = /MicroMessenger/i.test(ua);
  const chrome = /CriOS/i.test(ua) || /Chrome/i.test(ua);
  const tiktok = mobile && /aweme/i.test(ua);
  const weibo = mobile && /Weibo/i.test(ua);
  const safari = ios && !chrome && !wx && !weibo && !tiktok && /Safari|Macintosh/i.test(ua);
  const firefox = /Firefox/.test(ua);
  const win = /windows|win32|win64|wow32|wow64/.test(uas);
  const linux = /linux/.test(uas);
  return {
    ua,
    mobile,
    android,
    ios,
    mac,
    wx,
    chrome,
    iphone,
    ipad,
    safari,
    tiktok,
    weibo,
    win,
    linux,
    firefox
  };
};
const blockSuiteFeatureFlags = z.object({
  enable_expand_database_block: z.boolean(),
  enable_bultin_ledits: z.boolean()
});
const runtimeFlagsSchema = z.object({
  enablePlugin: z.boolean(),
  builtinPlugins: z.array(z.string()),
  enableTestProperties: z.boolean(),
  enableBroadcastChannelProvider: z.boolean(),
  enableDebugPage: z.boolean(),
  changelogUrl: z.string(),
  downloadUrl: z.string(),
  // see: tools/workers
  imageProxyUrl: z.string(),
  enablePreloading: z.boolean(),
  enableNewSettingModal: z.boolean(),
  enableNewSettingUnstableApi: z.boolean(),
  enableSQLiteProvider: z.boolean(),
  enableNotificationCenter: z.boolean(),
  enableCloud: z.boolean(),
  enableCaptcha: z.boolean(),
  enableEnhanceShareMode: z.boolean(),
  enablePayment: z.boolean(),
  enablePageHistory: z.boolean(),
  enableCopilot: z.boolean(),
  // this is for the electron app
  serverUrlPrefix: z.string(),
  enableMoveDatabase: z.boolean(),
  editorFlags: blockSuiteFeatureFlags,
  appVersion: z.string(),
  editorVersion: z.string(),
  appBuildType: z.union([
    z.literal("stable"),
    z.literal("beta"),
    z.literal("internal"),
    z.literal("canary")
  ])
});
function setupGlobal() {
  if (globalThis.$AFFINE_SETUP) {
    return;
  }
  runtimeFlagsSchema.parse(runtimeConfig);
  let environment2;
  const isDebug = process.env.NODE_ENV === "development";
  if (isServer) {
    environment2 = {
      isDesktop: false,
      isBrowser: false,
      isServer: true,
      isDebug
    };
  } else {
    const uaHelper = new UaHelper(navigator);
    environment2 = {
      origin: window.location.origin,
      isDesktop,
      isBrowser: true,
      isServer: false,
      isDebug,
      isLinux: uaHelper.isLinux,
      isMacOs: uaHelper.isMacOs,
      isSafari: uaHelper.isSafari,
      isWindows: uaHelper.isWindows,
      isFireFox: uaHelper.isFireFox,
      isMobile: uaHelper.isMobile,
      isChrome: uaHelper.isChrome,
      isIOS: uaHelper.isIOS
    };
    if (environment2.isChrome && !environment2.isIOS) {
      assertEquals(environment2.isSafari, false);
      assertEquals(environment2.isFireFox, false);
      environment2 = {
        ...environment2,
        isSafari: false,
        isFireFox: false,
        isChrome: true,
        chromeVersion: uaHelper.getChromeVersion()
      };
    }
  }
  globalThis.environment = environment2;
  globalThis.$AFFINE_SETUP = true;
}
setupGlobal();
const windowFrameStyleOptions = [
  "frameless",
  "NativeTitleBar"
];
const dateFormatOptions = [
  "MM/dd/YYYY",
  "dd/MM/YYYY",
  "YYYY-MM-dd",
  "YYYY.MM.dd",
  "YYYY/MM/dd",
  "dd-MMM-YYYY",
  "dd MMMM YYYY"
];
const fontStyleOptions = [
  { key: "Sans", value: "var(--affine-font-sans-family)" },
  { key: "Serif", value: "var(--affine-font-serif-family)" },
  { key: "Mono", value: "var(--affine-font-mono-family)" }
];
const appSettingBaseAtom = atomWithStorage("affine-settings", {
  clientBorder: environment.isDesktop && !environment.isWindows,
  fullWidthLayout: false,
  windowFrameStyle: "frameless",
  fontStyle: "Sans",
  dateFormat: dateFormatOptions[0],
  startWeekOnMonday: false,
  enableBlurBackground: true,
  enableNoisyBackground: true,
  autoCheckUpdate: true,
  autoDownloadUpdate: true
});
const appSettingEffect = atomEffect((get) => {
  var _a2;
  const settings = get(appSettingBaseAtom);
  if (environment.isDesktop) {
    console.log("set config", settings);
    (_a2 = window.apis) == null ? void 0 : _a2.updater.setConfig({
      autoCheckUpdate: settings.autoCheckUpdate,
      autoDownloadUpdate: settings.autoDownloadUpdate
    }).catch((err) => {
      console.error(err);
    });
  }
});
const appSettingAtom = atom(
  (get) => {
    get(appSettingEffect);
    return get(appSettingBaseAtom);
  },
  (_get, set, apply) => {
    set(appSettingBaseAtom, (prev) => {
      const next = typeof apply === "function" ? apply(prev) : apply;
      return { ...prev, ...next };
    });
  }
);
const currentPageIdAtom = atom(null);
const loadedPluginNameAtom = atom([]);
export {
  _setCurrentStore,
  appSettingAtom,
  contentLayoutAtom,
  currentPageIdAtom,
  dateFormatOptions,
  fontStyleOptions,
  getCurrentStore,
  loadedPluginNameAtom,
  windowFrameStyleOptions
};
